import type { CheckDigitStateCode, StateCode } from "./types";

const factors11erVerfahren: Record<CheckDigitStateCode, number[]> = {
  "DE-BY": [0, 5, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-BE-A": [0, 0, 0, 0, 0, 7, 6, 5, 8, 4, 3, 2],
  "DE-BE-B": [0, 0, 2, 9, 0, 8, 7, 6, 5, 4, 3, 2],
  "DE-BB": [0, 5, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-HB": [0, 0, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-HH": [0, 0, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-MV": [0, 5, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-NI": [0, 0, 2, 9, 0, 8, 7, 6, 5, 4, 3, 2],
  "DE-NW": [0, 3, 2, 1, 0, 7, 6, 5, 4, 3, 2, 1],
  "DE-SL": [0, 5, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-SN": [0, 5, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-ST": [0, 5, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
  "DE-TH": [0, 5, 4, 3, 0, 2, 7, 6, 5, 4, 3, 2],
};

const factorsModified11erVerfahren = [0, 0, 1, 2, 0, 1, 2, 1, 2, 1, 2, 1];
const summands2erVerfahren = [0, 0, 9, 8, 0, 7, 6, 5, 4, 3, 2, 1];
const factors2erVerfahren = [0, 0, 512, 256, 0, 128, 64, 32, 16, 8, 4, 2];

function sumDigits(num: number): number {
  let sum = 0;
  let remaining = num;
  while (remaining) {
    sum += remaining % 10;
    remaining = Math.floor(remaining / 10);
  }
  return sum;
}

/** Check digit using the 2er-Verfahren (DE-BW, DE-HE, DE-SH) */
export function getPruefziffer2er(normalizedDigits: string): number {
  const sums: number[] = [];
  for (let i = 0; i < 12; i++) {
    // biome-ignore lint/style/noNonNullAssertion: indices 0-11 are always valid for 12-element arrays
    sums.push(Number(normalizedDigits[i]) + summands2erVerfahren[i]!);
  }
  const sumsSingleDigits = sums.map((p) => p % 10);
  // biome-ignore lint/style/noNonNullAssertion: indices 0-11 are always valid for 12-element arrays
  const products = sumsSingleDigits.map((s, i) => s * factors2erVerfahren[i]!);
  let digitSums = products.map(sumDigits);
  while (digitSums.find((s) => s > 9)) {
    digitSums = digitSums.map(sumDigits);
  }
  const sum = digitSums.reduce((acc, current) => acc + current, 0);
  if (sum % 10 === 0) {
    return 0;
  }
  return 10 * Math.ceil(sum / 10) - sum;
}

/** Check digit using the modified 11er-Verfahren (DE-RP) */
export function getPruefzifferModified11er(normalizedDigits: string): number {
  const products: number[] = [];
  for (let i = 0; i < 12; i++) {
    products.push(
      // biome-ignore lint/style/noNonNullAssertion: indices 0-11 are always valid for 12-element arrays
      Number(normalizedDigits[i]) * factorsModified11erVerfahren[i]!
    );
  }
  const singleDigits = products.map((p) => (p > 9 ? (p % 10) + 1 : p));
  const sum = singleDigits.reduce((acc, current) => acc + current, 0);
  return 10 * Math.ceil(sum / 10) - sum;
}

/** Check digit using the 11er-Verfahren (most states) */
export function getPruefziffer11er(
  normalizedDigits: string,
  land: CheckDigitStateCode
): number {
  const factors = factors11erVerfahren[land];
  const products: number[] = [];
  for (let i = 0; i < 12; i++) {
    // biome-ignore lint/style/noNonNullAssertion: indices 0-11 are always valid for 12-element arrays
    products.push(Number(normalizedDigits[i]) * factors[i]!);
  }
  const sum = products.reduce((acc, current) => acc + current, 0);
  if (land === "DE-NW") {
    return sum % 11;
  }
  if (sum % 11 === 0) return 0;
  return 11 * Math.ceil(sum / 11) - sum;
}

/** Returns all valid check digits for the given normalized Steuernummer and states */
export function getPruefziffern(
  normalizedDigits: string,
  states: StateCode | StateCode[]
): number[] {
  const statesToCheck = Array.isArray(states) ? states : [states];

  if (statesToCheck.includes("DE-RP")) {
    return [getPruefzifferModified11er(normalizedDigits)];
  }
  if (
    statesToCheck.includes("DE-BW") ||
    statesToCheck.includes("DE-HE") ||
    statesToCheck.includes("DE-SH")
  ) {
    return [getPruefziffer2er(normalizedDigits)];
  }
  if (statesToCheck.includes("DE-BE")) {
    return [
      getPruefziffer11er(normalizedDigits, "DE-BE-A"),
      getPruefziffer11er(normalizedDigits, "DE-BE-B"),
    ];
  }
  return [
    getPruefziffer11er(
      normalizedDigits,
      statesToCheck[0] as unknown as CheckDigitStateCode
    ),
  ];
}
